<template>
    <!-- Patient Treatment Plan -->
    <v-expansion-panel focusable>
        <template v-slot:title>
            Treatment Plan
        </template>
        <v-expansion-panel-text>
            <div v-if="hasTreatmentPlan">
                <div v-if="numberOfPlans > 1" class="d-flex justify-end">
                    <v-btn icon @click="decrement">
                        <v-icon>mdi-arrow-left</v-icon>
                    </v-btn>
                    <v-btn icon @click="increment">
                        <v-icon>mdi-arrow-right</v-icon>
                    </v-btn>
                </div>
                Status: {{ getStatus }}
                <v-card>
                    <v-card-actions class="d-flex justify-end">
                        <v-btn outlined class="bg-aqua-teal" @click="navigateToContentSelection('new')">New</v-btn>
                    </v-card-actions>
                </v-card>
                <v-card class="bg-aqua-teal">
                    <v-card-actions class="d-flex justify-end">
                        <v-btn v-if="!getStatusIsActive" @click="activatePlan">Activate</v-btn>
                        <v-btn @click="dialog = true" icon>
                            <v-icon>mdi-delete-outline</v-icon>
                        </v-btn>

                        <!-- Deletion Confirmation Dialog -->
                        <v-dialog persistent v-model="dialog" max-width="300">
                            <v-card>
                                <v-card-title class="bg-aqua-teal">Confirmation</v-card-title>
                                <v-card-text>Are you sure you want to delete this plan?</v-card-text>
                                <v-card-actions>
                                    <v-btn class="bg-aqua-teal" @click="dialog = false">Cancel</v-btn>
                                    <v-btn class="bg-bright-orange" @click="deletePlan">Confirm</v-btn>
                                </v-card-actions>
                            </v-card>

                        </v-dialog>
                    </v-card-actions>
                    <v-card-title class="text-center text-wrap">{{ getPlanName }}</v-card-title>
                    <v-card-text>
                        {{ getDescription }}
                    </v-card-text>
                </v-card>
                <v-card>
                    <v-card-actions class="d-flex justify-end">
                        <v-btn outlined class="bg-aqua-teal" @click="navigateToContentSelection('edit')">Edit</v-btn>
                        <v-btn outlined class="bg-bright-orange text-white" @click="completionDialog = true">Complete</v-btn>
                    </v-card-actions>
                </v-card>
                <v-virtual-scroll :items="['1']" class="mt-2">
                    <v-row class="pa-8">
                        <v-col cols="12" md="6" no-gutters v-for="(content, index) in Object.keys(contents)"
                            :key="index">
                            <ItemCard :item="getContents[content]" :no-actions="true"
                                :href="getContents[content].link" :show-status="true"/>
                        </v-col>
                    </v-row>
                </v-virtual-scroll>
            </div>
            <div v-else class="d-flex justify-end">
                <v-btn outlined class="bg-aqua-teal" @click="navigateToContentSelection('new')">Create a Plan</v-btn>
            </div>
        </v-expansion-panel-text>
    </v-expansion-panel>

    <v-dialog v-model="completionDialog" persistent :max-width="300">
        <v-card>
            <v-card-title class="bg-aqua-teal text-wrap text-center">Confirm Plan Completion</v-card-title>
            <v-card-text>
                Your patient {{(allComplete) ? "has" : "has not"}} completed all of their assigned tasks in this plan. Confirm the completion of your patient's plan? 
            </v-card-text>
            <v-card-actions>
                <v-card-actions>
                    <v-btn class="bg-aqua-teal" @click="completionDialog = false">Back</v-btn>
                    <v-btn class="bg-bright-orange text-white" @click="completePatientPlan">Confirm</v-btn>
                </v-card-actions>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import axios from 'axios';
import ItemCard from './ItemCard.vue';
export default {
    name: "PatientPlanSubPanel",
    props: ['plan', 'plansList', 'patientId', 'providerId'],
    emits: ['deletePlan', 'finishedPlan', 'buildPlan'],
    data() {
        return {
            contents: {},
            dialog: false,
            tasksIds: [],
            index: 0,
            statuses: ['Active', 'Inactive', 'Completed'],
            completionDialog: false,
            allComplete: false, 
        }
    },
    components: {
        ItemCard
    },
    mounted() {
        this.fetchData();
    },
    methods: {
        closeDialog() {
            this.dialog = false;
        },
        async activatePlan() {
            axios.defaults.baseURL = process.env.VUE_APP_API_URL;
            try {
                // Retrieve old plan from plans list, if applicable
                var deactivatePlan = null
                for (let i = 0; i < this.plansList.length; i++) {
                    if (this.plansList[i].planStatus === "Active") {
                        deactivatePlan = this.plansList[i];
                        break;
                    }
                }

                if (deactivatePlan != null) {
                    // Update old plan to be inactive
                    deactivatePlan.planStatus = "Inactive";
                    await axios.put(`/api/therapyplan/${deactivatePlan.planId}`, deactivatePlan);
                }
            } catch (error) {
                console.log("No previously active plan.");
            }

            try {
                // Retrieve plan to make active
                const activePlan = this.plansList[this.index];

                // Update plan to be active
                activePlan.planStatus = "Active";

                // Save to database
                await axios.put(`/api/therapyplan/${activePlan.planId}`, activePlan);
            } catch (error) {
                console.log(error);
            }


        },
        async fetchData() {
            if (this.plansList?.length > 0) {
                try {
                    axios.defaults.baseURL = process.env.VUE_APP_API_URL;
                    const response = await axios.get(`/api/assignedtask/${this.plansList[this.index].planId}`);
                    const tasks = response.data;
                    let completedTasks = 0;
                    let ids = [];
                    this.tasksIds = [];
                    console.log(tasks);
                    for (let i = 0; i < tasks.length; i++) {
                        ids.push(tasks[i].contentId);
                        this.tasksIds.push(tasks[i].assignedTaskId);
                        if (tasks[i].status.toLowerCase() === "complete") completedTasks += 1;
                    }

                    let contents = {};
                    for (let i = 0; i < ids.length; i++) {
                        const response = await axios.get(`/api/unifiedcontent/contentid/${ids[i]}`);
                        contents[ids[i]] = response.data;
                        contents[ids[i]]['status'] = tasks[i].status;
                    }
                    this.contents = this.alphabetical_sort_object_of_objects(contents, "contentBlock");
                    this.allComplete = completedTasks === ids.length;
                } catch (error) {
                    console.log(error);
                }
            }
        },
        async navigateToContentSelection(type) {
            try {
                axios.defaults.baseURL = process.env.VUE_APP_API_URL;

                /* Set content selection flags */
                var isNew = (type === "new") ? true : false;
                var isEdit = (type === "edit") ? true : false;

                // Set plan id, if plan exists
                var planId = "null";
                if (this.plansList?.length > 0) {
                    planId = this.plansList[this.index].planId;
                }

                // Post the request body for encryption
                const response = await axios.post('/api/encryption/encrypt/array', {
                    plainData: [planId],
                    headers: {
                        'Content-Type': 'application/json'
                    }
                });

                // Retrieve encrypted values for storing in request body. 
                const values = response.data;

                // Route to Content Selection Page using Query for persistent state.
                // this.$router.push({
                //     query: {
                //         patientId: this.patientId,
                //         planId: values[0],
                //         isEdit: isEdit,
                //         isNew: isNew,
                //     }
                // });
                // this.$router.push({ query: { test: 'test', anotherKey: 'anotherValue' } });

                // Emit query object to parent
                var query = {
                        patientId: this.patientId,
                        planId: values[0],
                        isEdit: isEdit,
                        isNew: isNew,
                        origin: "patient-panel"
                    }
                this.$emit('buildPlan', query);
                
            } catch (error) {
                console.log(error);
            }
        },
        async completePatientPlan(){
            axios.defaults.baseURL = process.env.VUE_APP_API_URL;
            try {
                await axios.put(`api/therapyplan/update-status/${this.plansList[this.index].planId}?status=complete`);
                this.$emit('finishedPlan');
            } catch(error){
                console.log(error);
            }
            this.completionDialog = false;
        },
        increment() {
            if (this.index < this.numberOfPlans - 1) {
                this.index++;
            } else {
                this.index = 0;
            }
        },
        decrement() {
            if (this.index > 0) {
                this.index--;
            } else {
                this.index = (this.numberOfPlans - 1 > 0) ? this.numberOfPlans - 1 : 0;
            }
        },
        async deletePlan() {
            if (this.plansList?.length > 0) {
                try {
                    axios.defaults.baseURL = process.env.VUE_APP_API_URL;
                    const body = {};
                    body['Ids'] = this.tasksIds;
                    await axios.delete(`/api/assignedtask/delete`, { data: body });
                    var index = this.index;
                    this.index = 0;
                    await axios.delete(`/api/therapyplan/${this.plansList[index].planId}`);
                    this.$emit('deletePlan', this.plansList[index].planId);
                } catch (error) {
                    console.log(error);
                }
            }
            this.closeDialog();
        },

        alphabetical_sort_object_of_objects(data, attr) {
            var arr = [];

            // Extract identification and sort keys
            for (let prop in data) {
                if (Object.hasOwn(data, prop)) {
                    let obj = {};
                    obj[prop] = data[prop];
                    obj.tempSortName = data[prop][attr].toLowerCase();
                    arr.push(obj);
                }
            }

            // Arrange each object by the sort key
            arr.sort((a, b) => {
                var at = a.tempSortName,
                    bt = b.tempSortName;
                return at > bt ? 1 : (at < bt ? -1 : 0);
            });

            // Arrange each actual object by the sorted order
            var result = [];
            for (var i = 0, l = arr.length; i < l; i++) {
                let obj = arr[i];
                delete obj.tempSortName;
                for (let prop in obj) {
                    if (Object.hasOwn(obj, prop)) {
                        var id = prop;
                    }
                }
                var item = obj[id];
                result.push(item);
            }
            return result;
        }
    },
    computed: {
        existsPlan() {
            return this.plansList?.length > 0 || false;
        },
        numberOfPlans() {
            return this.plansList?.length || 0;
        },
        getContents() {
            return this.contents;
        },
        /** The following computed components handle 
        * undefined fields for displaying patient 
        * information.
        */
        getDescription() {
            if (this.existsPlan) {
                if (this.plansList[this.index].planDescription != null) {
                    return this.plansList[this.index].planDescription;
                }
            }
            return "Unavailable.";
        },
        getStatus() {
            if (this.existsPlan) {
                if (this.plansList[this.index].planStatus != null) {
                    return this.plansList[this.index].planStatus;
                }
            }
            return "Unavailable.";
        },
        getStatusIsActive() {
            return this.getStatus === "Active";
        },
        getPlanName() {
            if (this.existsPlan) {
                if (this.plansList[this.index].planName != null) {
                    return this.plansList[this.index].planName;
                }
            }
            return "Unavailable.";
        },
        hasTreatmentPlan() {
            if (this.existsPlan) {
                this.fetchData();
                return true;
            } else {
                return false;
            }
        },
    }
}
</script>